import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { materialCategories, getMaterialByKey, mockMaterials } from '@mocks/malzemeler';
import { ExportDropdown } from '@components/ui';
import { useExportExcel } from '@hooks';
import MaterialTab from './components/MaterialTab';
import MaterialModal from './components/MaterialModal';
import useScrollNavigation from '@hooks/useScrollNavigation';
import clsx from 'clsx';

export default function Materials() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [activeTab, setActiveTab] = useState('beton');
    const [showAddModal, setShowAddModal] = useState(false);
    const [categories, setCategories] = useState(materialCategories);

    const material = getMaterialByKey(activeTab);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        if (!material) return;
        exportToExcel({
            data: (material.schedule || []).map(s => ({
                'Tarih': s.date,
                'Miktar': `${s.quantity} ${material.unit}`,
                'Firma': s.supplier,
                'Tutar': s.cost,
                'Durum': s.status === 'delivered' ? 'Teslim Edildi' : s.status === 'scheduled' ? 'Planlandı' : 'Bekliyor',
            })),
            columns: [
                { header: 'Tarih', key: 'Tarih' },
                { header: 'Miktar', key: 'Miktar' },
                { header: 'Firma', key: 'Firma' },
                { header: 'Tutar', key: 'Tutar', format: 'currency' },
                { header: 'Durum', key: 'Durum' },
            ],
            fileName: `malzeme_${activeTab}`,
            sheetName: material.label,
        });
    };

    // Scroll navigation hook
    const {
        scrollRef,
        isDragging,
        showLeftArrow,
        showRightArrow,
        scrollContainerStyle,
        handlers,
        scrollToDirection,
    } = useScrollNavigation({ scrollAmount: 250 });

    const handleAddMaterial = (newMaterial) => {
        const newCategory = {
            key: newMaterial.key,
            label: newMaterial.label,
            color: newMaterial.color,
        };
        setCategories(prev => [...prev, newCategory]);
        mockMaterials[newMaterial.key] = newMaterial.data;
        setActiveTab(newMaterial.key);
        setShowAddModal(false);
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Malzeme Takibi</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Malzeme teslimat ve işçilik takibi'
                            : `${selectedConstruction?.name || ''} - Malzeme takibi`}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <ExportDropdown
                        onExportExcel={handleExportExcel}
                        isExporting={isExporting}
                    />
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium min-h-[44px]"
                    >
                        <Plus size={18} />
                        <span>Yeni Malzeme</span>
                    </button>
                </div>
            </div>

            {/* Premium Category Tabs with Arrows */}
            <div className="relative">
                {/* Left Arrow */}
                {showLeftArrow && (
                    <button
                        onClick={() => scrollToDirection('left')}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg bg-white dark:bg-neutral-800 border border-border-light hover:scale-110 transition-transform"
                    >
                        <ChevronLeft size={20} className="text-primary" />
                    </button>
                )}

                {/* Right Arrow */}
                {showRightArrow && (
                    <button
                        onClick={() => scrollToDirection('right')}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg bg-white dark:bg-neutral-800 border border-border-light hover:scale-110 transition-transform"
                    >
                        <ChevronRight size={20} className="text-primary" />
                    </button>
                )}

                {/* Scrollable Tabs Container */}
                <div
                    ref={scrollRef}
                    className={clsx(
                        "flex gap-2 py-2 px-8 md:px-10",
                        isDragging ? "cursor-grabbing" : "cursor-grab"
                    )}
                    style={scrollContainerStyle}
                    {...handlers}
                >
                    {categories.map((cat) => {
                        const isActive = activeTab === cat.key;
                        return (
                            <button
                                key={cat.key}
                                onClick={() => !isDragging && setActiveTab(cat.key)}
                                className={clsx(
                                    "relative px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap min-h-[44px]",
                                    "border-2 shadow-sm",
                                    isActive
                                        ? "bg-primary text-white border-primary shadow-primary/25"
                                        : "bg-white dark:bg-neutral-800 text-text-secondary border-border-light hover:border-primary/50 hover:text-primary"
                                )}
                            >
                                {cat.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Sekme İçeriği */}
            {material ? (
                <MaterialTab material={material} />
            ) : (
                <div className="text-center py-12 text-text-muted">
                    <p>Bu malzeme için henüz veri bulunmuyor.</p>
                </div>
            )}

            {/* Yeni Malzeme Modal */}
            <MaterialModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSubmit={handleAddMaterial}
            />
        </div>
    );
}
